import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { api } from '../api';
import { compressImage } from '../image';
import type { ItemState } from '../types';

type TemplateItem = { id: string; roomLabel: string; itemLabel: string; perimeter: 'COMMON' | 'PRIVATE'; requiresBalcony: boolean };
type Room = { id: string; name: string; hasBalcony: boolean };

const STATE_OPTIONS: Array<{ value: ItemState; label: string }> = [
  { value: 'GOOD', label: 'Bien' },
  { value: 'AVERAGE', label: 'Moyen' },
  { value: 'POOR', label: 'Mauvais' },
];

/**
 * Signaler une dégradation remarquée hors état des lieux (locataire qui
 * signale un robinet qui fuit, chaise cassée repérée en passant). Sans cet
 * écran, une telle anomalie n'avait nulle part où aller avant le prochain EDL,
 * des mois plus tard.
 */
export function ReportAnomalyPanel({ onReported, embedded = false, basePath = '/api/admin/conditions' }: {
  onReported: () => Promise<void>;
  /** Posé dans une fenêtre : plus de bloc dépliant, et le catalogue se charge d'emblée. */
  embedded?: boolean;
  /** Bascule entre l'accès administrateur et le lien temporaire d'une intervenante. */
  basePath?: string;
}) {
  const [catalog, setCatalog] = useState<{ items: TemplateItem[]; rooms: Room[] } | null>(null);
  const [scope, setScope] = useState<'COMMON' | 'PRIVATE'>('PRIVATE');
  const [roomId, setRoomId] = useState('');
  const [templateItemId, setTemplateItemId] = useState('');
  const [state, setState] = useState<ItemState>('AVERAGE');
  const [comment, setComment] = useState('');
  const [reportedBy, setReportedBy] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function loadCatalog(): Promise<void> {
    if (catalog) return;
    try {
      const result = await api<{ items: TemplateItem[]; rooms: Room[] }>(`${basePath}/catalog`);
      setCatalog(result);
      setRoomId(result.rooms[0]?.id ?? '');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement du catalogue impossible.');
    }
  }

  const selectedRoom = catalog?.rooms.find((room) => room.id === roomId) ?? null;

  const availableItems = useMemo(() => {
    if (!catalog) return [];
    return catalog.items.filter((item) => (
      item.perimeter === scope && (item.perimeter === 'COMMON' || !item.requiresBalcony || selectedRoom?.hasBalcony)
    ));
  }, [catalog, scope, selectedRoom]);

  useEffect(() => {
    if (availableItems.length > 0 && !availableItems.some((item) => item.id === templateItemId)) {
      setTemplateItemId(availableItems[0]!.id);
    }
  }, [availableItems, templateItemId]);

  // En fenêtre, il n'y a plus de repli à ouvrir : sans ce chargement le
  // formulaire resterait vide sur « Chargement… ».
  useEffect(() => { if (embedded) void loadCatalog(); }, [embedded]);

  /** Partagé par les deux commandes, qui ne diffèrent que par leur `capture`. */
  function collectPhotos(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? []);
    if (files.length) setPhotos((current) => [...current, ...files]);
    event.target.value = '';
  }

  async function submit(): Promise<void> {
    if (!templateItemId) return;
    setBusy(true);
    setError('');
    setDone(false);
    try {
      const created = await api<{ id: string }>(basePath, {
        method: 'POST',
        body: JSON.stringify({
          roomId: scope === 'COMMON' ? null : roomId,
          templateItemId,
          state,
          comment: comment.trim(),
          reportedBy: reportedBy.trim(),
        }),
      });
      // Les photos suivent le signalement plutôt que de l'accompagner : le
      // constat existe d'abord, les images s'y rattachent ensuite. Si l'envoi
      // d'une image échoue, l'anomalie reste enregistrée.
      for (const file of photos) {
        const compressed = await compressImage(file);
        const form = new FormData();
        form.append('photo', compressed, `constat-${Date.now()}.jpg`);
        await api(`${basePath}/${created.id}/photos`, { method: 'POST', body: form });
      }
      setComment('');
      setPhotos([]);
      setDone(true);
      await onReported();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Signalement impossible.');
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <>
      <p className="section-intro">
        Pour une dégradation remarquée en cours de bail, hors état des lieux : un locataire signale un problème, vous
        repérez quelque chose en passant. Elle rejoint la liste ci-dessous, datée de ce signalement.
      </p>
      {!catalog && !error && <p className="muted">Chargement…</p>}
      {catalog && (
        <div className="form-grid">
          <fieldset className="segmented-field">
            <legend>Où</legend>
            <div className="segmented">
              <button type="button" className={scope === 'PRIVATE' ? 'active' : ''} onClick={() => setScope('PRIVATE')}>Une chambre</button>
              <button type="button" className={scope === 'COMMON' ? 'active' : ''} onClick={() => setScope('COMMON')}>Partie commune</button>
            </div>
          </fieldset>
          {scope === 'PRIVATE' && (
            <label>Chambre
              <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
                {catalog.rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
              </select>
            </label>
          )}
          <label>Élément
            <select value={templateItemId} onChange={(event) => setTemplateItemId(event.target.value)}>
              {availableItems.map((item) => (
                <option key={item.id} value={item.id}>{item.roomLabel} · {item.itemLabel}</option>
              ))}
            </select>
          </label>
          <fieldset className="segmented-field">
            <legend>État</legend>
            <div className="segmented">
              {STATE_OPTIONS.map((option) => (
                <button type="button" key={option.value} className={state === option.value ? 'active' : ''} onClick={() => setState(option.value)}>
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="span-2">Description
            <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} placeholder="Ce qui est arrivé, où précisément…" />
          </label>
          <label>Signalé par <span className="muted">(facultatif)</span>
            <input value={reportedBy} onChange={(event) => setReportedBy(event.target.value)} placeholder="vous, un locataire…" />
          </label>
          <div className="span-2">
            <span className="field-label">Photos <span className="muted">(facultatif)</span></span>
            <div className="photo-actions">
              <label className="button secondary compact">
                Prendre une photo
                <input type="file" accept="image/*" capture="environment" hidden onChange={collectPhotos} />
              </label>
              <label className="button secondary compact">
                Importer des photos
                <input type="file" accept="image/*" multiple hidden onChange={collectPhotos} />
              </label>
            </div>
            {photos.length > 0 && (
              <ul className="pending-photos">
                {photos.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    {file.name}
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Retirer"
                      onClick={() => setPhotos((current) => current.filter((_, position) => position !== index))}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" className="button primary compact" disabled={busy || !templateItemId} onClick={() => void submit()}>
            {busy ? 'Envoi…' : "Signaler l'anomalie"}
          </button>
        </div>
      )}
      {done && <p className="inline-message success">Anomalie enregistrée : elle apparaît dans la liste ci-dessous.</p>}
      {error && <div className="inline-message error" role="alert">{error}</div>}
    </>
  );

  if (embedded) return body;
  return (
    <details className="access-panel" onToggle={(event) => { if (event.currentTarget.open) void loadCatalog(); }}>
      <summary>Signaler une anomalie</summary>
      {body}
    </details>
  );
}
